import { isEqual, startsWith, debounce } from 'lodash';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import { Component } from 'react';
import { FaRegFolderOpen } from 'react-icons/fa';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { IoSearchSharp } from 'react-icons/io5';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { showDialog } from '../../../core/actions';
import DataTable from '../../../core/components/DataTable';
import { hasRoleInOperatorGroup, RoleTypes } from '../../../core/components/Permission';
import TableActionButtonsCell from '../../../core/components/TableActionButtonsCell';
import { getApiStatus, toString } from '../../../core/utils';
import { selectSession, selectSite } from '../../../store/authReducer';
import { Container, Row, Col } from 'reactstrap';
import { fetchEmployees } from '../../employees/actions';
import { selectEmployees } from '../../employees/reducer';
import { completeTaskAOT } from '../../overview/actions';
import * as actions from '../actions';
import { buildQuery, getLastCompleted } from '../helpers';
import { Interval, TaskType } from '../models';
import {
  selectPage,
  selectPageSize,
  selectTaskInEdit,
  selectTaskLocations,
  selectTaskPrefixes,
  selectTasks,
  selectTotal,
} from '../reducer';
import { TaskCompletionStatus } from '../types';
import { Drawer } from '../../../core/components/Drawer';
import { FilterPlacement, PageFilters } from '../../../core/components/PageFilters';
import { TaskDetails } from '../components/TaskDetails';
import { TASK_DRAWER_WIDTH } from '../types';

export class Tasks extends Component {
  static propTypes = {
    tasks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    employees: PropTypes.arrayOf(PropTypes.shape({})),
    fetchTasks: PropTypes.func.isRequired,
    showDialog: PropTypes.func.isRequired,
    intl: PropTypes.shape({}).isRequired,
    addTask: PropTypes.func.isRequired,
    addQuickTask: PropTypes.func.isRequired,
    fetchEmployees: PropTypes.func.isRequired,
    completeTaskAOT: PropTypes.func.isRequired,
    total: PropTypes.number,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    taskPrefixList: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number,
        message: PropTypes.string,
      })
    ),
    taskLocationList: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number,
        message: PropTypes.string,
        orderNumber: PropTypes.number,
      })
    ),
    getTaskPrefixes: PropTypes.func,
    getTaskLocations: PropTypes.func,
    session: PropTypes.shape({
      role: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  };

  static defaultProps = { taskPrefixList: [], taskLocationList: [] };

  state = {
    typeFilter: '',
    prefixFilter: '',
    searchTerm: '',
    sortQuery: {},
    selectedTask: {},
    toggleCollapse: false,
    taskInEditExpanded: false,
    highlightedRow: 0,
    isAdding: false,
  };

  getTaskQuery = (queryParameters = {}) => {
    const defaultQuery = { page: 0 };

    return buildQuery(defaultQuery, queryParameters, this.state);
  };

  fetchTasks = (queryParameters) => {
    const taskQuery = this.getTaskQuery(queryParameters);
    this.props.fetchTasks(taskQuery);
  };

  debouncedFetchTasks = debounce(() => this.fetchTasks(), 1000);

  componentWillUnmount() {
    this.debouncedFetchTasks.cancel();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const arePropsEqual = isEqual(this.props, nextProps);
    const areStatesEqual = isEqual(this.state, nextState);

    return !arePropsEqual || !areStatesEqual;
  }

  componentDidMount() {
    this.fetchTasks();

    this.props.fetchEmployees();

    if (!this.props.taskPrefixList.length) {
      this.props.getTaskPrefixes();
    }

    if (!this.props.taskLocationList.length) {
      this.props.getTaskLocations();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { typeFilter, prefixFilter, sortQuery, selectedTask, searchTerm } = this.state;

    const immediateChange =
      typeFilter !== prevState.typeFilter ||
      prefixFilter !== prevState.prefixFilter ||
      sortQuery !== prevState.sortQuery ||
      this.props.site !== prevProps.site;

    if (immediateChange) {
      this.debouncedFetchTasks.cancel();
      this.fetchTasks();
    } else if (searchTerm !== prevState.searchTerm) {
      this.debouncedFetchTasks();
    }

    if (
      typeFilter !== prevState.typeFilter &&
      typeFilter === toString(TaskType, TaskType.Project)
    ) {
      this.props.getTaskPrefixes();
    }

    if (selectedTask !== prevState.selectedTask) {
      this.setState({ toggleCollapse: false });
    }
  }

  getPaginationInfo() {
    return {
      total: this.props.total,
      page: this.props.page,
      pageSize: this.props.pageSize,
      goToPage: this.getTasksPage,
    };
  }

  getTasksPage = (page) => {
    this.fetchTasks({ page });
  };

  getTaskCompletionText = (task) => {
    return task.interval !== Interval.CarCount
      ? this.props.intl.formatMessage({ id: 'willSkipNextTime' })
      : this.props.intl.formatMessage({ id: 'willUpdateCarCountTask' });
  };

  taskIsValidForPreCompletion = ({ latestCompletion }) => {
    if (latestCompletion) {
      const pending = toString(TaskCompletionStatus, TaskCompletionStatus.Pending);
      if (latestCompletion.status === pending) {
        // Check if the completion is expired
        return moment().utc().isAfter(moment(latestCompletion.scheduleEnd).utc());
      }
    }
    return true;
  };

  handleDone = (event, { _id }) => {
    const task = this.props.tasks.find((t) => t.id === _id);
    this.props.fetchEmployees();

    const dialogProps = {
      onReady: this.props.completeTaskAOT,
      task,
      text: this.getTaskCompletionText(task),
      title: this.props.intl.formatMessage({ id: 'completeTaskAOT' }),
      isValid: this.taskIsValidForPreCompletion(task),
    };

    this.props.showDialog('COMPLETE_TASK_AOT', dialogProps);
  };

  rowClickFunction = (data) => {
    this.setState((state) => ({
      ...state,
      selectedTask: { ...data },
      highlightedRow: data._id,
    }));
  };

  getRowClickInfo = () => ({
    callback: (data) => {
      this.rowClickFunction(data);
    },
  });

  initializeDatatable = (tasks) => {
    const { intl, session } = this.props;

    const data = tasks.map((task) => ({
      _id: task.id,
      _taskId: task.id,
      _showActions: !task.isEssentialTask
        ? true
        : hasRoleInOperatorGroup(session, RoleTypes.OperatorGroupAdmin, task.groupId),
      name: task.name,
      _subTitle: task.subTitle,
      intervalDescription: task.getIntervalDescription(intl.formatMessage),
      start: moment(task.start).format('MM/DD/YYYY'),
      end: task.end && moment(task.end).format('MM/DD/YYYY'),
      lastCompleted: getLastCompleted(task.latestCompletion, this.props.employees),
      _resource: task.resource,
    }));

    const headers = [
      { name: intl.formatMessage({ id: 'name' }), databaseProperty: 'name' },
      { name: intl.formatMessage({ id: 'interval' }), databaseProperty: 'intervalDescription' },
      { name: intl.formatMessage({ id: 'startDate' }), databaseProperty: 'start' },
      { name: intl.formatMessage({ id: 'endDate' }), databaseProperty: 'end' },
      { name: intl.formatMessage({ id: 'lastCompleted' }) },
    ];
    headers.push(
      {
        name: this.props.intl.formatMessage({ id: 'resource' }),
        cell: {
          component: (props) => {
            const url = startsWith(props.rowData._resource, 'http')
              ? props.rowData._resource
              : `https://${props.rowData._resource}`;
            return (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {props.rowData._resource && (
                  <i
                    className="icon icon-link px-4 text-info"
                    title={`${props.rowData._resource}`}
                  />
                )}
              </a>
            );
          },
        },
      },
      {
        name: intl.formatMessage({ id: 'action' }),
        cell: {
          component: TableActionButtonsCell,
          mixIns: {
            buttons: [
              {
                title: 'Done',
                class: 'button green small',
                buttonAction: this.handleDone,
              },
            ],
          },
        },
      }
    );

    return {
      data,
      rowClickInfo: this.getRowClickInfo(),
      headers,
      fetchSortedData: this.fetchSortedTasks,
    };
  };

  fetchSortedTasks = (sortParameters) => {
    if (sortParameters) {
      this.setState({ sortQuery: sortParameters });
    }
  };

  getTaskTypeOptions = () => {
    const { intl } = this.props;
    return [
      {
        value: toString(TaskType, TaskType.Maintenance),
        label: intl.formatMessage({ id: 'maintenance' }),
      },
      {
        value: toString(TaskType, TaskType.Project),
        label: intl.formatMessage({ id: 'workOrder', defaultMessage: 'Work Order' }),
      },
      {
        value: toString(TaskType, TaskType.OpenChecklist),
        label: intl.formatMessage({ id: 'openChecklist' }),
      },
      {
        value: toString(TaskType, TaskType.ClosingChecklist),
        label: intl.formatMessage({ id: 'closingChecklist' }),
      },
      {
        value: toString(TaskType, TaskType.PreventativeMaintenance),
        label: intl.formatMessage({ id: 'preventativeMaintenance' }),
      },
      {
        value: toString(TaskType, TaskType.CleaningList),
        label: intl.formatMessage({ id: 'cleaningList' }),
      },
      {
        value: toString(TaskType, TaskType.HourlyInspection),
        label: intl.formatMessage({ id: 'hourlyInspection' }),
      },
      {
        value: toString(TaskType, TaskType.ExpressInterior),
        label: intl.formatMessage({ id: 'expressInterior' }),
      },
    ];
  };

  handleFilterChange = (vals) => {
    const typeFilter = Array.isArray(vals.typeFilter)
      ? vals.typeFilter[0] || ''
      : vals.typeFilter || '';
    const prefixFilter = Array.isArray(vals.prefixFilter)
      ? vals.prefixFilter[0] || ''
      : vals.prefixFilter || '';
    this.setState({
      typeFilter,
      prefixFilter: typeFilter !== 'Project' ? '' : prefixFilter,
      searchTerm: vals.searchTerm || '',
    });
  };

  removeSelectedTask = () => {
    this.setState((state) => ({ ...state, selectedTask: {}, taskInEditExpanded: false }));
  };

  setSelectedTask = (task) => {
    this.setState({ selectedTask: task });
  };

  setTaskInEditExpanded = () => {
    this.setState({ taskInEditExpanded: !this.state.taskInEditExpanded });
  };

  handleCloseDrawer = () => {
    this.setState((state) => ({ ...state, selectedTask: {}, isAdding: false }));
  };

  handleAddClick = () => {
    this.setState((state) => ({ ...state, isAdding: true }));
  };

  onSubmit = (task) => {
    if (task.quickAddTask) {
      const { name, description, addAnother } = task;
      const quickTask = { name, description, addAnother };
      this.props.addQuickTask(quickTask);
    } else {
      this.props.addTask(task);
    }
  };
  render() {
    const { intl, taskPrefixList } = this.props;
    const { typeFilter } = this.state;

    const taskTypeOptions = this.getTaskTypeOptions();

    const prefixOptions = (taskPrefixList || []).map((p) => ({
      value: String(p.type),
      text: p.message,
      selected: String(this.state.prefixFilter) === String(p.type),
    }));

    const filterConfigs = [
      {
        id: 'searchTerm',
        type: 'search',
        name: intl.formatMessage({ id: 'search', defaultMessage: 'Search' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <IoSearchSharp />,
        searchTerm: this.state.searchTerm,
        debounceMs: 0,
        onChange: (value) =>
          this.handleFilterChange({
            searchTerm: value || '',
            typeFilter: this.state.typeFilter,
            prefixFilter: this.state.prefixFilter,
          }),
      },
      {
        id: 'typeFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'taskType' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        multiSelect: false,
        icon: <FaRegCircleCheck />,
        options: taskTypeOptions.map((option) => ({
          value: option.value,
          text: option.label,
          selected: String(this.state.typeFilter) === String(option.value),
        })),
        onChange: (values) =>
          this.handleFilterChange({
            searchTerm: this.state.searchTerm,
            typeFilter: values[0] || '',
            prefixFilter: this.state.prefixFilter,
          }),
      },
      ...(typeFilter === toString(TaskType, TaskType.Project)
        ? [
            {
              id: 'prefixFilter',
              type: 'select',
              name: intl.formatMessage({ id: 'taskPrefix' }),
              placement: FilterPlacement.Chiclet,
              defaultVisible: true,
              multiSelect: false,
              icon: <FaRegFolderOpen />,
              options: prefixOptions,
              onChange: (values) =>
                this.handleFilterChange({
                  searchTerm: this.state.searchTerm,
                  typeFilter: this.state.typeFilter,
                  prefixFilter: values[0] || '',
                }),
            },
          ]
        : []),
    ];

    return (
      <Container fluid className="no-padding">
        <Row className="todays-tasks">
          <Col>
            <div className="filters-actions-container mb-3">
              <PageFilters filters={filterConfigs} maxChicletFilters={4} />
              <button className="button primary" onClick={this.handleAddClick}>
                <span className="desktop-label">
                  {this.props.intl.formatMessage({ id: 'addTask' })}
                </span>
                <span className="mobile-label">+</span>
              </button>
            </div>
          </Col>
        </Row>
        <div className="tasks-table">
          <DataTable
            showLoader={this.props.isLoading}
            paginationInfo={this.getPaginationInfo()}
            highlightedRow={this.state.highlightedRow}
            scrolling
            {...this.initializeDatatable(
              this.props.tasks.filter((task) => task.type !== TaskType.Project)
            )}
          />
        </div>
        <Drawer
          expanded={this.state.selectedTask.hasOwnProperty('_id') || this.state.isAdding}
          maximized={false}
          expandedWidth={TASK_DRAWER_WIDTH}
          onBackdropClick={this.handleCloseDrawer}
          overlay
        >
          {(this.state.selectedTask.hasOwnProperty('_id') || this.state.isAdding) && (
            <TaskDetails
              taskId={this.state.selectedTask._id ?? null}
              isNew={this.state.isAdding}
              onClose={this.handleCloseDrawer}
              onSave={this.fetchTasks}
              onDelete={this.fetchTasks}
            />
          )}
        </Drawer>
      </Container>
    );
  }
}

const mapStateToProps = (state) => {
  const isLoading = getApiStatus(state, 'FETCH_TASKS');
  return {
    site: Number(selectSite(state)),
    tasks: selectTasks(state),
    initialTaskInEdit: selectTaskInEdit(state),
    total: selectTotal(state),
    page: selectPage(state),
    pageSize: selectPageSize(state),
    session: selectSession(state),
    employees: selectEmployees(state),
    taskPrefixList: selectTaskPrefixes(state),
    taskLocationList: selectTaskLocations(state),
    isLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      ...actions,
      showDialog,
      fetchEmployees,
      completeTaskAOT,
    },
    dispatch
  );
};

const enhance = compose(connect(mapStateToProps, mapDispatchToProps), withRouter, injectIntl);

export default enhance(Tasks);
